import { useFeatureIsOn } from '@growthbook/growthbook-react'
import { ThemeProvider } from '@mui/material'
import { theme as bsTheme } from 'bs-unified-ui'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo } from 'react'
import { REACT_APP_MODE } from '~/constants/environment'
import { FLAGS } from '~/constants/flags'
import { STORE_TYPES } from '~/constants/store'
import { useAuthPlugins, useAuthState } from '~/modules/auth/redux/hook'
import RouteProvider from '~/providers/RouteProvider'
import { useGetStore } from '~/redux/hooks/store'
import { useDebugMode } from '~/utils/hooks/useDebugMode'

const RouteWithStore = ({ routeConfig, ...props }) => {
  const activeStore = useGetStore()
  const authState = useAuthState()
  const authPlugins = useAuthPlugins()

  // LIST_FLAG_RELEASE_PRODUCTION => list flag ready for production
  const LIST_FLAG_RELEASE_PRODUCTION = []

  // LIST_FLAG_USE_DEBUG_MODE => list flag ready for testing
  const LIST_FLAG_USE_DEBUG_MODE = []
  const isDebugMode = useDebugMode()
  const isProductNewUI = useFeatureIsOn(FLAGS.ACD_12337)
  const isOrderNewUI = useFeatureIsOn(FLAGS.ACD_12446)
  const isPantryStoreRevampUI =
    useFeatureIsOn(FLAGS.PANTRY_STORE_REVAMP_UI) &&
    activeStore?.isPantry &&
    activeStore?.enableRevampUIForPantryStore

  const useNewUI = useMemo(() => {
    if (routeConfig.featureFlag) {
      if (LIST_FLAG_RELEASE_PRODUCTION.includes(routeConfig.featureFlag)) {
        return true
      }

      if (routeConfig.featureFlag === FLAGS.ACD_12337) {
        return isProductNewUI
      }

      if (routeConfig.featureFlag === FLAGS.ACD_12446) {
        return isOrderNewUI
      }

      if (LIST_FLAG_USE_DEBUG_MODE.includes(routeConfig.featureFlag)) {
        return isDebugMode
      }

      return REACT_APP_MODE === 'development'
    }

    return true
  }, [routeConfig.featureFlag, isDebugMode, isProductNewUI, isOrderNewUI])

  const pantryAllowed = routeConfig.pantryAllowedStoreTypes
  const storeType = activeStore?.storeType
  const isPantryRouteAllowed =
    !pantryAllowed?.length || pantryAllowed.includes(storeType)

  const readOnly =
    routeConfig.editRoles &&
    activeStore?.storeId &&
    !routeConfig.editRoles(activeStore?.storeId, authPlugins)(authState.data)

  const renderComponent = useCallback(
    (routeProps) => {
      const rProps = {
        ...routeProps.match?.params,
        ...routeConfig.xProps,
        url: routeProps.location?.pathname,
        path: routeProps.match?.path,
        store: activeStore,
        readOnly,
      }
      const pantryDebugAllowed = !routeConfig.pantryDebugGate || isDebugMode
      if (
        isPantryStoreRevampUI &&
        routeConfig.pantryComponent &&
        pantryDebugAllowed &&
        isPantryRouteAllowed
      ) {
        return (
          <RouteProvider value={rProps}>
            <routeConfig.pantryComponent {...rProps} {...routeProps} />
          </RouteProvider>
        )
      }
      switch (activeStore?.storeType) {
        case STORE_TYPES.COVA:
          if (useNewUI && routeConfig.covaComponentV2) {
            return (
              <ThemeProvider theme={bsTheme}>
                <RouteProvider value={rProps}>
                  <routeConfig.covaComponentV2 {...rProps} {...routeProps} />
                </RouteProvider>
              </ThemeProvider>
            )
          }

          if (routeConfig.covaComponent) {
            return (
              <RouteProvider value={rProps}>
                <routeConfig.covaComponent {...rProps} {...routeProps} />
              </RouteProvider>
            )
          }
        // eslint-disable-next-line no-fallthrough
        case STORE_TYPES.GREENLINE:
          if (useNewUI && routeConfig.greenlineComponentV2) {
            return (
              <ThemeProvider theme={bsTheme}>
                <RouteProvider value={rProps}>
                  <routeConfig.greenlineComponentV2
                    {...rProps}
                    {...routeProps}
                  />
                </RouteProvider>
              </ThemeProvider>
            )
          }

          if (routeConfig.greenlineComponent) {
            return (
              <RouteProvider value={rProps}>
                <routeConfig.greenlineComponent {...rProps} {...routeProps} />
              </RouteProvider>
            )
          }
        // eslint-disable-next-line no-fallthrough
        case STORE_TYPES.SHOPIFY:
          if (routeConfig.shopifyComponent) {
            return (
              <RouteProvider value={rProps}>
                <routeConfig.shopifyComponent {...rProps} {...routeProps} />
              </RouteProvider>
            )
          }
        case STORE_TYPES.FLOWHUB:
          if (routeConfig.flowhubComponentV2) {
            return (
              <ThemeProvider theme={bsTheme}>
                <RouteProvider value={rProps}>
                  <routeConfig.flowhubComponentV2 {...rProps} {...routeProps} />
                </RouteProvider>
              </ThemeProvider>
            )
          }
          if (routeConfig.flowhubComponent) {
            return (
              <RouteProvider value={rProps}>
                <routeConfig.flowhubComponent {...rProps} {...routeProps} />
              </RouteProvider>
            )
          }
        case STORE_TYPES.LIGHTSPEED:
          if (routeConfig.lightspeedComponentV2) {
            return (
              <ThemeProvider theme={bsTheme}>
                <RouteProvider value={rProps}>
                  <routeConfig.lightspeedComponentV2
                    {...rProps}
                    {...routeProps}
                  />
                </RouteProvider>
              </ThemeProvider>
            )
          }

          if (routeConfig.lightspeedComponent) {
            return (
              <RouteProvider value={rProps}>
                <routeConfig.lightspeedComponent {...rProps} {...routeProps} />
              </RouteProvider>
            )
          }
        // eslint-disable-next-line no-fallthrough
        default:
          return (
            <RouteProvider value={rProps}>
              <routeConfig.component {...rProps} {...routeProps} />
            </RouteProvider>
          )
      }
    },
    [
      activeStore,
      routeConfig,
      readOnly,
      useNewUI,
      isPantryStoreRevampUI,
      isPantryRouteAllowed,
      isDebugMode,
    ],
  )

  return <>{renderComponent(props)}</>
}

RouteWithStore.propTypes = {
  routeConfig: PropTypes.shape().isRequired,
}

export default RouteWithStore
